import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/shared/lib/utils';
import { Text } from '../text';

const avatarVariants = cva('relative flex shrink-0 overflow-hidden rounded-full', {
  variants: {
    size: {
      sm: 'h-8 w-8 text-sm', // 32px - Small
      tiny: 'h-6 w-6 text-xs', // 24px - Tiny
      xs: 'h-5 w-5 text-[8px]', // 20px - Extra Tiny
    },
  },
  defaultVariants: {
    size: 'sm',
  },
});

type AvatarProps = React.ComponentPropsWithRef<typeof AvatarPrimitive.Root> &
  VariantProps<typeof avatarVariants> & {
    isLoading?: boolean;
    userName?: string | undefined;
    src?: string;
  };

export const Avatar = ({ className, isLoading, userName, src, size, ...props }: AvatarProps) => (
  <AvatarPrimitive.Root
    tabIndex={0}
    className={cn(
      avatarVariants({ size }),
      isLoading && 'animate-pulse bg-muted',
      'focus:ring-2 focus:ring-focus-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
      className,
    )}
    {...props}
  >
    {src && (
      <AvatarPrimitive.Image
        src={src}
        alt={userName || 'avatar'}
        className="aspect-square h-full w-full object-cover"
      />
    )}
    <AvatarPrimitive.Fallback
      className={cn('flex h-full w-full items-center justify-center rounded-full', 'bg-brand-100')}
    >
      {!isLoading && (
        <Text variant="label" color="link" className="cursor-pointer">
          {userName?.slice(0, 2).toUpperCase()}
        </Text>
      )}
    </AvatarPrimitive.Fallback>
  </AvatarPrimitive.Root>
);
